import React from 'react';
import {
    Factory, Building, Lightbulb, Truck, ShoppingBag,
    FileText, Shield, LineChart, Clock, Database, Server,
    Activity, Network, Bot, Zap, BarChart2, Settings, Workflow
} from 'lucide-react';

const AUTOMATION_SOLUTIONS = [
    {
        title: "Manufacturing Process Automation",
        challenge: "Manual assembly causing 23% production delays and 17% inconsistent quality issues",
        solution: "Robotic process automation with digital twins for real-time monitoring and predictive maintenance",
        result: "89% reduction in assembly errors and 35% increase in production throughput"
    },
    {
        title: "Warehouse Workflow Optimization",
        challenge: "Order fulfillment taking 4.2 hours on average with 22% error rate in picking operations",
        solution: "Automated workflow orchestration with AI-powered pick path optimization and validation",
        result: "Order fulfillment time reduced to 47 minutes with 99.8% accuracy"
    },
    {
        title: "Intelligent Document Processing",
        challenge: "12 full-time employees processing 30,000+ documents monthly with 8% error rate",
        solution: "End-to-end document automation with OCR, ML classification, and exception handling",
        result: "95% of documents processed without human intervention, 87% cost reduction"
    },
    {
        title: "Customer Service Automation",
        challenge: "Support team handling 4,800+ tickets monthly with 32 hour average response time",
        solution: "Automated ticket classification, routing, and response generation with human escalation",
        result: "75% tickets auto-resolved, 92% reduction in response time, 38% increased CSAT"
    },
    {
        title: "Financial Reconciliation Engine",
        challenge: "Finance team spending 480+ hours monthly on manual reconciliation with 9% discrepancy rate",
        solution: "Automated transaction matching system with anomaly detection and exception workflows",
        result: "91% automatic reconciliation rate with 99.7% accuracy, freeing 440+ hours monthly"
    },
    {
        title: "IT Service Desk Automation",
        challenge: "IT team overwhelmed with 3,700+ monthly tickets, 70% being routine password resets and access requests",
        solution: "Self-service portal with automated user verification, permission management and provisioning",
        result: "83% reduction in routine tickets, 68% faster resolution times, 42% lower IT support costs"
    },
    {
        title: "Compliance Monitoring System",
        challenge: "Regulatory audits requiring 240+ staff hours with incomplete coverage of compliance risks",
        solution: "Continuous automated compliance monitoring with real-time alerting and audit trail generation",
        result: "100% compliance visibility, 72% reduction in audit preparation time, zero regulatory findings"
    },
    {
        title: "Supply Chain Orchestration",
        challenge: "Manual supply chain management leading to $2.7M in excess inventory and 28% stockouts",
        solution: "Automated demand forecasting, inventory optimization, and supplier communication",
        result: "Inventory carrying costs reduced by 41%, stockouts down by 87%, supplier lead times improved by 35%"
    },
    {
        title: "Energy Consumption Management",
        challenge: "Facilities operating with 31% energy waste and unpredictable utility costs across 12 locations",
        solution: "Automated environmental controls with load balancing, scheduling, and usage monitoring",
        result: "43% reduction in energy costs and 57% decrease in carbon emissions within first year"
    }
];

// Get color classes based on index
const getColorClasses = (index, element) => {
    const colors = ["blue", "amber", "purple", "emerald", "indigo", "pink", "teal", "orange", "violet"];
    const color = colors[index % colors.length];

    const colorMap = {
        blue: {
            text: "text-blue-400",
            bgLight: "bg-blue-400/10",
            bgMedium: "bg-blue-500/20",
            bgGradient: "from-blue-600 to-blue-400",
            border: "border-blue-500/30",
            shadow: "shadow-blue-500/30"
        },
        amber: {
            text: "text-amber-400",
            bgLight: "bg-amber-400/10",
            bgMedium: "bg-amber-500/20",
            bgGradient: "from-amber-600 to-amber-400",
            border: "border-amber-500/30",
            shadow: "shadow-amber-500/30"
        },
        purple: {
            text: "text-purple-400",
            bgLight: "bg-purple-400/10",
            bgMedium: "bg-purple-500/20",
            bgGradient: "from-purple-600 to-purple-400",
            border: "border-purple-500/30",
            shadow: "shadow-purple-500/30"
        },
        emerald: {
            text: "text-emerald-400",
            bgLight: "bg-emerald-400/10",
            bgMedium: "bg-emerald-500/20",
            bgGradient: "from-emerald-600 to-emerald-400",
            border: "border-emerald-500/30",
            shadow: "shadow-emerald-500/30"
        },
        indigo: {
            text: "text-indigo-400",
            bgLight: "bg-indigo-400/10",
            bgMedium: "bg-indigo-500/20",
            bgGradient: "from-indigo-600 to-indigo-400",
            border: "border-indigo-500/30",
            shadow: "shadow-indigo-500/30"
        },
        pink: {
            text: "text-pink-400",
            bgLight: "bg-pink-400/10",
            bgMedium: "bg-pink-500/20",
            bgGradient: "from-pink-600 to-pink-400",
            border: "border-pink-500/30",
            shadow: "shadow-pink-500/30"
        },
        teal: {
            text: "text-teal-400",
            bgLight: "bg-teal-400/10",
            bgMedium: "bg-teal-500/20",
            bgGradient: "from-teal-600 to-teal-400",
            border: "border-teal-500/30",
            shadow: "shadow-teal-500/30"
        },
        orange: {
            text: "text-orange-400",
            bgLight: "bg-orange-400/10",
            bgMedium: "bg-orange-500/20",
            bgGradient: "from-orange-600 to-orange-400",
            border: "border-orange-500/30",
            shadow: "shadow-orange-500/30"
        },
        violet: {
            text: "text-violet-400",
            bgLight: "bg-violet-400/10",
            bgMedium: "bg-violet-500/20",
            bgGradient: "from-violet-600 to-violet-400",
            border: "border-violet-500/30",
            shadow: "shadow-violet-500/30"
        }
    };

    return colorMap[color][element];
};

// Function to get the appropriate icon based on index
const getAutomationSolutionIcon = (index) => {
    const icons = [
        <Bot className="w-10 h-10" />,
        <Workflow className="w-10 h-10" />,
        <FileText className="w-10 h-10" />,
        <Zap className="w-10 h-10" />,
        <Database className="w-10 h-10" />,
        <Server className="w-10 h-10" />,
        <Shield className="w-10 h-10" />,
        <Truck className="w-10 h-10" />,
        <Lightbulb className="w-10 h-10" />
    ];
    return icons[index % icons.length];
};

// Automation Solution Card with pipeline aesthetic
const AutomationSolutionCard = ({ solution, index }) => (
    <div className="group relative overflow-hidden rounded-xl transition-all duration-300 hover:translate-y-[-8px]">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-800/90 to-slate-900/90 backdrop-blur-sm shadow-lg" />
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-blue-600/20 transition-opacity duration-500" />
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />

        {/* Pipeline connector on left side */}
        <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1/2 w-6 h-20 bg-slate-800 rounded-r-md border border-blue-500/30 overflow-hidden">
            <div className="absolute inset-y-0 right-0 w-1 bg-blue-500/30"></div>
            <div className="absolute top-1/2 left-0 w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
        </div>

        <div className="relative p-6 flex flex-col h-full">
            <div className={`bg-gradient-to-tr from-slate-800 to-slate-700 p-3 rounded-lg border border-slate-600/30 self-start mb-5`}>
                <div className={getColorClasses(index, "text")}>
                    {getAutomationSolutionIcon(index)}
                </div>
            </div>

            <h3 className="text-xl font-bold text-white mb-3">{solution.title}</h3>

            <div className="text-slate-300 leading-relaxed space-y-2">
                <p><span className={`${getColorClasses(index, "text")} font-medium`}>Challenge:</span> {solution.challenge}</p>
                <p><span className={`${getColorClasses(index, "text")} font-medium`}>Solution:</span> {solution.solution}</p>
                <p><span className={`${getColorClasses(index, "text")} font-medium`}>Result:</span> {solution.result}</p>
            </div>

            {/* Data flow indicators at bottom */}
            <div className="mt-auto pt-6 w-full">
                <div className="w-full h-1 bg-slate-700/50 rounded-full overflow-hidden">
                    <div className={`h-full bg-gradient-to-r ${getColorClasses(index, "bgGradient")} w-0 group-hover:w-full transition-all duration-1000 rounded-full`}></div>
                </div>
                <div className="mt-3 flex justify-between items-center">
                    <div className="flex items-center">
                        <div className={`w-2 h-2 ${getColorClasses(index, "text")} rounded-full mr-1 animate-pulse`}></div>
                        <span className={`text-xs ${getColorClasses(index, "text")}`}>
                            {index === 0 && "Manual Processes"}
                            {index === 1 && "Fulfillment Delays"}
                            {index === 2 && "Document Backlog"}
                            {index === 3 && "Support Bottleneck"}
                            {index === 4 && "Financial Errors"}
                            {index === 5 && "IT Overload"}
                            {index === 6 && "Compliance Gaps"}
                            {index === 7 && "Supply Chain Issues"}
                            {index === 8 && "Energy Waste"}
                        </span>
                    </div>
                    <div className="flex items-center">
                        <span className={`text-xs ${getColorClasses(index, "text")}`}>
                            {index === 0 && "Robotic Automation"}
                            {index === 1 && "Workflow Optimization"}
                            {index === 2 && "Document Processing"}
                            {index === 3 && "Service Automation"}
                            {index === 4 && "Reconciliation Engine"}
                            {index === 5 && "Service Automation"}
                            {index === 6 && "Compliance System"}
                            {index === 7 && "Supply Orchestration"}
                            {index === 8 && "Energy Management"}
                        </span>
                        <div className={`w-2 h-2 ${getColorClasses(index, "text")} rounded-full ml-1 animate-pulse`}></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

const AutomationSolutionsSection = () => {
    return (
        <section className="relative z-10 py-24 px-4 overflow-hidden">
            {/* Decorative background elements */}
            <div className="absolute inset-0 -z-10 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 -left-40 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl"></div>
            </div>

            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-16">
                    <h2 className="text-5xl md:text-6xl font-bold mb-8 tracking-tight">
                        <span className="text-white">Enterprise </span>
                        <span className="bg-gradient-to-r from-blue-400 via-blue-300 to-purple-400 bg-clip-text text-transparent">
                            Automation
                        </span>
                    </h2>

                    <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
                        Real-world implementations of our intelligent automation systems delivering measurable operational efficiency
                    </p>
                </div>

                {/* Automation Solutions Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 auto-rows-fr">
                    {AUTOMATION_SOLUTIONS.map((solution, index) => (
                        <AutomationSolutionCard
                            key={index}
                            solution={solution}
                            index={index}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default AutomationSolutionsSection;